import { Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ProgressLoaderComponent } from '../common/progress-loader/progress-loader.component'
import { TranslatePipe } from '@ngx-translate/core'
import { FileUploadComponent, UploadFailure } from '../file-upload/file-upload.component'
import { DocumentUploadInfo, truncateStringFromMiddle } from '../utils'
import { MAX_AGENDA_SIZE_IN_BYTES } from '../meeting-document-upload/meeting-document-upload.component'
import { AgendaUploadStatus, TemplateSourceType } from '../meetings.service'
import { ToastService } from '../toast.service'

@Component({
  selector: 'app-template-source-picker',
  standalone: true,
  imports: [MatIconModule, MatTooltipModule, ProgressLoaderComponent, TranslatePipe, FileUploadComponent],
  templateUrl: './template-source-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './template-source-picker.component.scss',
})
export class TemplateSourcePickerComponent {
  @Input()
  canEdit: boolean = true
  @Input()
  maxAgendaSize: number = MAX_AGENDA_SIZE_IN_BYTES
  @Input()
  status: AgendaUploadStatus = 'idle'
  @Input()
  failureMessageKey: string | null = null
  @Input()
  uploadedFileName: string | null = null
  @Input()
  templateSourceType: TemplateSourceType | null = null
  @Input()
  requiresConfirmation: boolean = false

  @Output()
  agendaFileChange = new EventEmitter<DocumentUploadInfo | null>()
  @Output()
  uploadStarted = new EventEmitter<void>()
  @Output()
  confirmationRequested = new EventEmitter<void>()
  @Output()
  createNewClicked = new EventEmitter<void>()
  @Output()
  loadSavedClicked = new EventEmitter<void>()

  @ViewChild('fileUpload')
  fileUploadComponent: FileUploadComponent | undefined

  private toastService = inject(ToastService)

  isDragging = false

  get isUploadInProgress(): boolean {
    return this.status === 'uploading' || (this.fileUploadComponent?.isUploading ?? false)
  }

  get canStartUpload(): boolean {
    return this.canEdit && !this.isUploadInProgress && this.status !== 'checking'
  }

  get displayFileName(): string | null {
    return this.uploadedFileName !== null ? truncateStringFromMiddle(this.uploadedFileName, 30) : null
  }

  onUploadCardClick() {
    if (this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.openFileDialog()
    }
  }

  openFileDialog() {
    if (this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.openFileDialog()
    }
  }

  onFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    this.agendaFileChange.emit(documentUploadInfo)
  }

  onUploadFailed({ messageKey, params }: UploadFailure) {
    this.toastService.show(messageKey, params)
  }

  onDragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = true
  }

  onDragLeave() {
    this.isDragging = false
  }

  onDrop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false
    const files = event.dataTransfer?.files ?? null
    if (files !== null && files.length > 0 && this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.fileInputChanged({ target: { files } })
    }
  }
}
